import React from "react";
import { Mail } from "lucide-react";
import { fmtDateTime } from "@/lib/format";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

/**
 * Reads either invoice-style fields (sent_at / sent_to / sent_email_id / sent_by)
 * or last_emailed_* fields used by pro-forma and receipt records, and returns a
 * unified `{at, to, providerId, by}` shape. Returns null when nothing was sent.
 */
export function readEmailAudit(record) {
  if (!record) return null;
  const at = record.sent_at || record.last_emailed_at;
  const to = record.sent_to || record.last_emailed_to;
  const providerId = record.sent_email_id || record.last_email_provider_id;
  const by = record.sent_by || record.last_emailed_by;
  if (!at && !to && !providerId) return null;
  return { at, to, providerId, by };
}

/**
 * Compact chip suitable for a table cell — shows a mail icon + short timestamp.
 * Hovers reveal recipient + Resend provider id via tooltip.
 */
export function SentEmailChip({ record, testId }) {
  const audit = readEmailAudit(record);
  if (!audit) return <span className="text-slate-300" data-testid={testId}>—</span>;
  const shortId = audit.providerId ? `${audit.providerId.slice(0, 10)}…` : null;
  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <span
            className="inline-flex items-center gap-1 text-xs text-slate-600 cursor-help"
            data-testid={testId}
          >
            <Mail className="w-3 h-3 text-emerald-600" aria-hidden="true" />
            <span className="font-mono">{fmtDateTime(audit.at)}</span>
          </span>
        </TooltipTrigger>
        <TooltipContent side="left" className="max-w-xs text-xs">
          <div className="space-y-1">
            <div>
              <span className="text-slate-400">To</span>{" "}
              <span className="font-mono">{audit.to || "—"}</span>
            </div>
            {shortId && (
              <div>
                <span className="text-slate-400">Resend id</span>{" "}
                <span className="font-mono select-all">{audit.providerId}</span>
              </div>
            )}
            <div>
              <span className="text-slate-400">At</span>{" "}
              <span className="font-mono">{fmtDateTime(audit.at)}</span>
            </div>
          </div>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

/**
 * Full block for a detail sheet — recipient + provider id + timestamp.
 */
export function SentEmailBlock({ record, testId = "sent-email-block" }) {
  const audit = readEmailAudit(record);
  if (!audit) return null;
  return (
    <div
      className="text-xs border-t border-slate-100 pt-3 space-y-1"
      data-testid={testId}
    >
      <div className="text-slate-500 uppercase tracking-widest text-[10px]">Last emailed</div>
      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-slate-700">
        <span data-testid={`${testId}-to`}>
          <span className="text-slate-400">To</span>{" "}
          <span className="font-mono">{audit.to || "—"}</span>
        </span>
        <span data-testid={`${testId}-at`}>
          <span className="text-slate-400">At</span>{" "}
          <span className="font-mono">{fmtDateTime(audit.at)}</span>
        </span>
        {audit.providerId && (
          <span data-testid={`${testId}-provider-id`}>
            <span className="text-slate-400">Resend id</span>{" "}
            <span className="font-mono select-all">{audit.providerId}</span>
          </span>
        )}
      </div>
    </div>
  );
}
